/**
 * kc-slide-group — behaviour of blocks/kc-slide-group.liquid (hero carousel and every other
 * slide group): infinite-scroll cloning, interval auto-scroll and its pause/resume wiring with
 * the segmented scrollbar.
 *
 * This was an inline <script> emitted by every slide group (≈8 KB each, four on the homepage),
 * with the block id and two settings interpolated by Liquid. The same code now lives here once;
 * each block passes its values through data attributes on the .slide-group-block element:
 *   data-block-id            block.id
 *   data-auto-scroll-type    block.settings.auto_scroll_type  ("interval" | "infinite" | …)
 *   data-scroll-interval     block.settings.scroll_interval, default 5 (seconds)
 *
 * Loaded with <script type="module">: evaluated once per page however many blocks emit the tag,
 * after theme.js has defined <scroll-carousel>.
 */

const initialised = new WeakSet();

function initSlideGroup(container) {
  if (!container || initialised.has(container)) return;
  // A slide group nested inside another group's infinite-scroll CLONE shares its block id with
  // the original; only the original is driven (same as the inline version, which looked the
  // block up by id and got the first match).
  if (container.closest('[id^="slide-content-clone-"]')) return;
  initialised.add(container);

  const BLOCK_ID = container.dataset.blockId;
  const SCROLL_AREA_ID = 'scroll-area-' + BLOCK_ID;
  const AUTO_SCROLL_TYPE = container.dataset.autoScrollType || '';
  const SCROLL_INTERVAL_SEC = parseFloat(container.dataset.scrollInterval) || 5;

  customElements.whenDefined('scroll-carousel').then(function () {
    const carousel = document.getElementById(SCROLL_AREA_ID);
    if (!container || !carousel) return;

    const slideContent = document.getElementById('slide-content-' + BLOCK_ID);

    function optimizeClonedMedia(rootNode) {
      rootNode.querySelectorAll('img').forEach(function (img) {
        img.loading = 'lazy';
        img.decoding = 'async';
        img.fetchPriority = 'low';
      });

      rootNode.querySelectorAll('video').forEach(function (video) {
        video.preload = 'none';
        video.removeAttribute('autoplay');
      });

      rootNode.querySelectorAll('iframe').forEach(function (iframe) {
        iframe.loading = 'lazy';
      });
    }

    function getTotalSlides() {
      return carousel.visibleItems.length || 1;
    }

    function updateTotalItems() {
      const realCount = getTotalSlides();
      container.style.setProperty('--total-items', realCount);
      return realCount;
    }

    // ============================================
    // INFINITE SCROLL
    // ============================================
    if (container.dataset.infiniteScroll === 'true') {
      requestAnimationFrame(function () {
        if (slideContent && !document.getElementById('slide-content-clone-' + BLOCK_ID)) {
          const clone = slideContent.cloneNode(true);
          clone.id = 'slide-content-clone-' + BLOCK_ID;
          optimizeClonedMedia(clone);
          slideContent.parentNode.appendChild(clone);
        }
      });

      let userInteracted = false;
      const pauseInfinite = function () {
        if (!userInteracted) {
          userInteracted = true;
          container.classList.add('scroll-paused');
        }
      };
      carousel.addEventListener('mousedown', pauseInfinite, { once: true });
      carousel.addEventListener('touchstart', pauseInfinite, { once: true });
    }

    // ============================================
    // INTERVAL AUTO-SCROLL
    // ============================================
    if (AUTO_SCROLL_TYPE === 'interval') {
      let intervalId = null;
      let isPaused = false;
      let currentIndex = 0;
      const intervalTime = SCROLL_INTERVAL_SEC * 1000;

      function scrollToIndex(index, animate) {
        const offset = carousel.itemOffset || 0;
        carousel.scrollTo({
          left: offset * index,
          behavior: animate ? 'smooth' : 'auto',
        });
      }

      function goToNext() {
        if (isPaused) return;
        const total = getTotalSlides();

        if (currentIndex + 1 >= total) {
          // Jump back to start without animation
          currentIndex = 0;
          scrollToIndex(0, false);
          return;
        }

        currentIndex++;
        scrollToIndex(currentIndex, true);
      }

      function startAutoScroll() {
        if (intervalId) clearInterval(intervalId);
        intervalId = setInterval(goToNext, intervalTime);
      }

      function stopAutoScroll() {
        if (!isPaused) {
          isPaused = true;
          if (intervalId) { clearInterval(intervalId); intervalId = null; }
          // Dispatch event so the scrollbar overlay pauses too
          document.dispatchEvent(new CustomEvent('carousel:pause', { detail: { carouselId: SCROLL_AREA_ID } }));
        }
      }

      function resumeAutoScroll() {
        if (isPaused) {
          isPaused = false;
          startAutoScroll();
          // Dispatch event so the scrollbar overlay resumes too
          document.dispatchEvent(new CustomEvent('carousel:resume', { detail: { carouselId: SCROLL_AREA_ID } }));
        }
      }

      // Sync currentIndex after manual swipe
      carousel.addEventListener('carousel:settle', function (e) {
        const rawIdx = e.detail && typeof e.detail === 'object' ? e.detail.index : e.detail;

        if (typeof rawIdx === 'number') {
          const settledItem = carousel.items[rawIdx];
          const realIdx = carousel.visibleItems.indexOf(settledItem);

          if (realIdx !== -1) {
            currentIndex = realIdx;
          }
        }
      });

      // Pause on manual touch/click
      carousel.addEventListener('mousedown', stopAutoScroll);
      carousel.addEventListener('touchstart', stopAutoScroll);

      // Stop on prev/next button click and re-sync
      document.querySelectorAll('button[aria-controls="' + SCROLL_AREA_ID + '"]').forEach(function (btn) {
        btn.addEventListener('click', stopAutoScroll);
      });

      // Segmented scrollbar integration: Listen to events dispatched from scrollbar.liquid
      document.addEventListener('carousel:pause', function (e) {
        if (e.detail && e.detail.carouselId === SCROLL_AREA_ID) {
           isPaused = true;
           if (intervalId) { clearInterval(intervalId); intervalId = null; }
        }
      });

      document.addEventListener('carousel:resume', function (e) {
        if (e.detail && e.detail.carouselId === SCROLL_AREA_ID) {
           isPaused = false;
           startAutoScroll();
        }
      });

      // LCP FIX: Defer auto-scroll until AFTER the browser has settled.
      // Previously the IntersectionObserver fired immediately (~100 ms after
      // load) and the carousel started rotating before the user — or
      // Lighthouse — finished measuring LCP. The carousel would auto-scroll
      // to slides 2/3/4 within 5–15 seconds, and Lighthouse would pick the
      // LATEST rendered slide as the LCP candidate, not the first slide that
      // is actually preloaded. RUM data showed the LCP image was on slide 4
      // (boundingRect.left = -1236, i.e. scrolled off-screen) with a 2740 ms
      // resourceLoadDelay because that slide had no preload.
      //
      // The fix: wait for `load` event AND requestIdleCallback before starting
      // auto-scroll. This guarantees the first slide is shown at full
      // quality for the entire LCP measurement window. After that, the
      // carousel resumes its merchant-configured behaviour.
      requestAnimationFrame(function () {
        updateTotalItems();
        currentIndex = 0;
        scrollToIndex(0, false);

        function armAutoScroll() {
          const observer = new IntersectionObserver(
            function (entries) {
              entries.forEach(function (entry) {
                if (entry.isIntersecting) {
                  startAutoScroll();
                  observer.disconnect(); // Fire once, then stop observing
                }
              });
            },
            { threshold: 0.5 } // 50% visible triggers start
          );
          observer.observe(container);
        }

        function deferredArm() {
          if (typeof window.requestIdleCallback === 'function') {
            window.requestIdleCallback(armAutoScroll, { timeout: 3000 });
          } else {
            setTimeout(armAutoScroll, 2500);
          }
        }

        if (document.readyState === 'complete') {
          deferredArm();
        } else {
          window.addEventListener('load', deferredArm, { once: true });
        }
      });
    } else {
      requestAnimationFrame(updateTotalItems);
    }
  });
}

function initAll(scope) {
  (scope || document).querySelectorAll('.slide-group-block[data-block-id]').forEach(initSlideGroup);
}

initAll(document);

// Theme editor: a re-rendered section brings new slide-group elements, and neither inline scripts
// nor this module run again for them.
document.addEventListener('shopify:section:load', (event) => {
  initAll(event.target || document);
});
